import React, { useEffect, useRef } from 'react';
import cn from 'classnames';

import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';
import OPE from 'js/on-page-editing';

import { IHtmlString } from 'source/NFR.types';

export const htmlThemes = defineTheme({
  narrow: 'html-string--theme-narrow',
  white: 'html-string--theme-white'
});
export type HtmlTheme = ThemeKeys<typeof htmlThemes>;

interface HtmlStringProps {
  className?: string;
  disableTabbing?: boolean;
  refProp?: React.MutableRefObject<HTMLElement | null>;
  theme?: HtmlTheme | HtmlTheme[];
  text: string;
  onPageEditing?: IHtmlString['onPageEditing'];
}

// NOTE: Do not use <> in this component. It breaks rendering to string in IE11 (which is done in map.jsx)
const HtmlString: React.FC<HtmlStringProps> = ({ className, onPageEditing, theme, text, disableTabbing, refProp }) => {
  const htmlStringRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (htmlStringRef.current) {
      // Wrap tables in divs to create horizontal scroll on overflowing tables.
      const tables = htmlStringRef.current.getElementsByTagName('table');

      for (const table of tables) {
        const parent = table.parentNode;
        const nextSibling = table.nextElementSibling;

        const wrapper = document.createElement('div');
        wrapper.classList.add('html-string__table-wrapper');
        wrapper.appendChild(table);

        parent?.insertBefore(wrapper, nextSibling);
      }
    }
  }, []);

  useEffect(() => {
    if (!disableTabbing) return;
    // Disable tabbing to links
    const links = Array.from(htmlStringRef.current?.getElementsByTagName('a') || []);
    links.forEach(link => {
      link.tabIndex = -1;
    });
  }, [disableTabbing]);

  if (!text) {
    return null;
  }

  return (
    <div
      ref={ref => {
        htmlStringRef.current = ref;
        if (refProp) {
          refProp.current = ref;
        }
      }}
      className={cn('html-string', htmlThemes(theme), className)}
      dangerouslySetInnerHTML={{
        __html: text
      }}
      {...OPE(onPageEditing?.text)}
    />
  );
};

HtmlString.displayName = 'HtmlString';

export default HtmlString;
